import { Inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ComplexSearch, FilterType, GroupPagedApiResponse, GroupsGetByIdRequestParams, GroupsGetBySearchRequestParams, GroupsService, SearchFilter, UserPagedApiResponse, UsersGetBySearchRequestParams, UsersService } from 'src/app/handweave-api';
import { AccessRecipe } from 'src/app/hickory-api';
import { ApiConfig } from '../../api-config/api-config';
import { BaseDirectoryService } from '../base/base-directory.service';
import { DirectoryGroup, DirectoryUser, DirectoryType, PagedDirectoryUsers, PagedDirectoryGroups } from '../base/models';
import { DIRECTORY_ID } from '../base/token';
import { getPageSlice } from '../../model/utility';
import { AlertBuilderService } from 'src/app/alert/shared/alert-builder';

@Injectable({ providedIn: 'root' }) // not a singleton -- see DirectoryInjector
export class HandweaveDirectoryService extends BaseDirectoryService {
  private readonly PAGE_SIZE = 500;

  constructor(
    @Inject(DIRECTORY_ID) injectedId: string | undefined,
    private usersApi: UsersService,
    private groupsApi: GroupsService,
  ) {
    super();
    this.directoryId = injectedId;
    this.directoryType = DirectoryType.Handweave;
  }

  getGroup(id: string, refresh?: boolean): Observable<DirectoryGroup> {
    if (!id || id === '-1') return of(null);
    const group = this.getCachedGroup(id);
    return new Observable<DirectoryGroup>((subscriber) => {
      if (!!group && !refresh) {
        subscriber.next(group);
        subscriber.complete();
      } else {
        const req: GroupsGetByIdRequestParams = { tenantId: ApiConfig.tenantId, id: id, };
        this.groupsApi.groupsGetById(req).subscribe({
          next: res => {
            const dg = DirectoryGroup.fromHandweave(res);
            dg.children = dg.children ?? group?.children;
            dg.root = group?.root ?? false;
            this.putGroup(dg);
            subscriber.next(dg);
            subscriber.complete();
          }, error: err => subscriber.error(err)
        });
      }
    });
  }

  getGroupChildren(id: string): Observable<DirectoryGroup[]> {
    if (!id) return of([]);
    return this.getSubGroups(id);
  }

  getGroupMembers(id: string): Observable<DirectoryUser[]> {
    if (!id) return of([]);
    throw new Error('Method not implemented.');
  }

  searchGroups(query: string): Observable<DirectoryGroup[]> {
    const limit = 10;
    return new Observable<DirectoryGroup[]>((subscriber) => {
      const filters: SearchFilter[] = [];
      if (this.directoryId) filters.push({ field: 'directoryId', value: this.directoryId, filterType: FilterType.Term });
      if (query?.length) filters.push({ fields: ['name' ], value: '*' + query + '*', filterType: FilterType.QueryString });
      const complexSearches: ComplexSearch[] = filters.length ? [ { oneOf: filters} ] : null;
      const req: GroupsGetBySearchRequestParams = {
        tenantId: ApiConfig.tenantId, page: 1, pageSize: limit,
        search: { searches: complexSearches },
      };
      this.groupsApi.groupsGetBySearch(req).subscribe({
        next: res => {
          const groups = res.items.map(g => {
            const dg = DirectoryGroup.fromHandweave(g);
            if (!this.getCachedGroup(dg.id)) this.putGroup(dg);
            return dg;
          });
          subscriber.next(groups);
          subscriber.complete();
        }, error: err => {
          subscriber.error(err);
        }
      });
    })
  }

  getGroupsById(ids: string[], page?: number, pageSize?: number): Observable<PagedDirectoryGroups> {
    page = page || 1;
    pageSize = pageSize || (ids?.length ?? 0);
    const res: PagedDirectoryGroups = { items: [], page: page, pageSize: pageSize, totalItemCount: pageSize };
    if (!ids?.length) return of(res);

    // check to see if the groups are in the cache
    let needFetch = false;
    const cached = ids.map(id => {
      const item = this.getCachedGroup(id);
      if (!item || item.isMissing === true) {
        needFetch = true;
        return null;
      } else {
        return item;
      }
    }).filter(x => !!x);
    if (!needFetch) {
      res.items = cached;
      return of(res);
    }

    const req: GroupsGetBySearchRequestParams = {
      tenantId: ApiConfig.tenantId, page: page, pageSize: pageSize,
      search: { searches: [{ 
        oneOf: [{ field: 'id', values: ids, filterType: FilterType.Ids }],
      }] },
    };

    return this.groupsApi.groupsGetBySearch(req).pipe(map(r => this.mapGroupResults(r)));
  }

  private mapGroupResults(res: GroupPagedApiResponse): PagedDirectoryGroups {
    const mapped: PagedDirectoryGroups = { page: res.page, pageSize: res.pageSize, totalItemCount: res.totalItemCount, };
    mapped.items = res?.items?.map(c => {
      const m = DirectoryGroup.fromHandweave(c);
      this.putGroup(m);
      return m;
    });
    return mapped;
  }

  private onGroupsData() {
    this.groupData$.next(this.cachedGroups);
    this.decrGroupRequests();
  }

  private onGroupsError(err: any) {
    this.groupData$.error(err);
    this.decrGroupRequests();
  }

  getRootGroups() {
    this.incrGroupRequests();
    const pageSize = this.PAGE_SIZE;
    const complexSearch: ComplexSearch = { notOneOf: [{ field: 'parentId', filterType: FilterType.Exist }] };
    if (this.directoryId) complexSearch.oneOf = [{ field: 'directoryId', value: this.directoryId, filterType: FilterType.Term }];
    const req: GroupsGetBySearchRequestParams = {
      tenantId: ApiConfig.tenantId, page: 1, pageSize: pageSize,
      search: { searches: [complexSearch] },
    };

    this.groupsApi.groupsGetBySearch(req).subscribe({
      next: (res) => {
        res.items?.forEach((g) => {
          const dg = DirectoryGroup.fromHandweave(g);
          dg.root = true;
          this.putGroup(dg);
        });
        this.onGroupsData();
      },
      error: (err) => {
        this.onGroupsError(err);
      }
    });
  }

  private getSubGroups(groupId: string): Observable<DirectoryGroup[]> {
    if (!groupId) return of([]);
    this.incrGroupRequests();
    const pageSize = this.PAGE_SIZE;
    const req: GroupsGetBySearchRequestParams = {
      tenantId: ApiConfig.tenantId, page: 1, pageSize: pageSize,
      search: { searches: [{ 
        oneOf: [{ field: 'parentId', values: [groupId], filterType: FilterType.Terms }],
      }] },
    };
    return this.groupsApi.groupsGetBySearch(req).pipe(
      map(res => {
        const data = res.items?.map(g => {
          const dg = DirectoryGroup.fromHandweave(g);
          this.putGroup(dg);
          return dg;
        });
        this.onGroupsData();
        return data;
      }),
      catchError((err) => {
        this.onGroupsError(err);
        return throwError(() => err);
      })
    );
  }

  getUsers(query?: string, page?: number, pageSize?: number): Observable<PagedDirectoryUsers> {
    const filters: SearchFilter[] = [];
    if (this.directoryId) filters.push({ field: 'directoryId', value: this.directoryId, filterType: FilterType.Term });
    if (query?.length) filters.push({ fields: ['lastName', 'firstName' ], value: query + '*', filterType: FilterType.QueryString });
    const complexSearches: ComplexSearch[] = filters.length ? [ { oneOf: filters} ] : null;
    const req: UsersGetBySearchRequestParams = {
      tenantId: ApiConfig.tenantId, page: page ?? 1, pageSize: pageSize ?? 25, search: { searches: complexSearches },
    };
    return this.getUserSearchResults(req);
  }

  getUsersById(ids: string[], groupIds?: string[], page?: number, pageSize?: number, _sort?: string): Observable<PagedDirectoryUsers> {
    page = page || 1;
    pageSize = pageSize || (ids?.length ?? 0);

    // FUTURE: handle sorting

    const res: PagedDirectoryUsers = { items: [], page: page, pageSize: pageSize, totalItemCount: pageSize };
    if (!ids?.length && !groupIds?.length) return of(res);

    // check to see if the users are in the cache
    if (!groupIds?.length) {
      let needFetch = false;
      const cached = ids.map(id => {
        const item = this.getCachedUser(id);
        if (!item || item.isMissing === true) {
          needFetch = true;
          return null;
        } else {
          return item;
        }
      }).filter(x => !!x);
      if (!needFetch) {
        res.items = cached;
        return of(res);
      }
    }

    // handle the case where we're resolving a large number of user IDs in batches
    if (ids?.length > pageSize) {
      ids = getPageSlice(ids, page, pageSize);
      page = 1;
    }

    const groupSearches: SearchFilter[] = [];
    if (groupIds.length) {
      groupIds.forEach(groupId => {
        const groupSearch =  {field: 'membership.path', value: '*' + groupId + '*', filterType: FilterType.Wildcard }
        groupSearches.push(groupSearch);
      });
    }

    const userSearches: SearchFilter[] = [];
    if (ids.length) {
      userSearches.push({ field: 'id', values: ids, filterType: FilterType.Ids });
    }

    const req: UsersGetBySearchRequestParams = {
      tenantId: ApiConfig.tenantId, page: page, pageSize: pageSize,
      search: { searches: [
        { oneOf: groupSearches },
        { oneOf: userSearches, }
      ]},
    };
    return this.getUserSearchResults(req);
  }

  getUserCountBySearch(_: AccessRecipe[]): Observable<number> {
    throw new Error('Method not implemented.');
  }
  getUsersBySearch(_0: AccessRecipe[], _1?: number, _2?: number, _3?: string): Observable<PagedDirectoryUsers> {
    throw new Error('Method not implemented.');
  }

  private getUserSearchResults(req: UsersGetBySearchRequestParams): Observable<PagedDirectoryUsers> {
    return this.usersApi.usersGetBySearch(req).pipe(map(res => this.mapUserResults(res)));
  }

  private mapUserResults(res: UserPagedApiResponse): PagedDirectoryUsers {
    const mapped: PagedDirectoryUsers = { page: res.page, pageSize: res.pageSize, totalItemCount: res.totalItemCount, };
    mapped.items = res?.items?.map(c => {
      const m = DirectoryUser.fromHandweave(c);
      this.putUser(m);
      return m;
    });
    return mapped;
  }

  totalRecipientCount(alertBuilder: AlertBuilderService): Observable<number> {
    const dir = alertBuilder?.alertRecipients?.directories?.find(d => d.directoryId === this.directoryId);
    if (!dir) return of(0);
    const userIds = dir.contactIds;

    let groupIds: string[] = dir.groupIds ?? [];
    groupIds = groupIds.concat(dir.mandatoryGroupIds ?? []);
    const groups = groupIds.map(id => { return this.getCachedGroup(id); }).filter(g => !!g);

    // get groups's counts via membership.path
    const searches: ComplexSearch[] = groups.map(g => {
      return { oneOf: [{ field: 'membership.path', value: g?.source?.path + '*', filterType: FilterType.Wildcard }]};
    });

    // get contacts counts by id
    if (userIds?.length) {
      searches.push({ oneOf: [{ field: 'id', values: userIds, filterType: FilterType.Ids, }] });
    }

    if (!searches.length) return of(0);

    const req: UsersGetBySearchRequestParams = {
      tenantId: ApiConfig.tenantId, page: 1, pageSize: 1,
      search: { searches: searches },
    };

    const json = JSON.stringify(req);
    const cachedCount = this.countRequestMap.get(json);
    if (cachedCount != null) return of(cachedCount);
    else return this.usersApi.usersGetBySearch(req).pipe(map(res => {
      const count = res.totalItemCount ?? 0;
      this.countRequestMap.set(json, count);
      return count;
    }));
  }
}
